
import React, { useState, useEffect } from 'react';
import { CheckCircle, ArrowRight, Package, Home, FileText, X, Download, Printer, Landmark } from 'lucide-react';
import { Order, Store } from '../types';
import { orderService } from '../services/orderService';
import { storage } from '../lib/storage';

interface OrderSuccessProps {
  orderId: string;
  onNavigateHome: () => void;
  onNavigateOrders: () => void;
}

const OrderSuccess: React.FC<OrderSuccessProps> = ({ orderId, onNavigateHome, onNavigateOrders }) => {
  const [showInvoice, setShowInvoice] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [store, setStore] = useState<Store | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      const o = await orderService.getOrderById(orderId);
      if (o) {
        setOrder(o);
        setStore(storage.getStoreConfig(o.storeId));
      }
    };
    fetchDetails();
  }, [orderId]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center relative">
      <div className="bg-white p-12 rounded-[40px] shadow-2xl border border-gray-100 space-y-8 animate-in zoom-in duration-500">
        <div className="flex justify-center">
          <div className="bg-green-100 p-6 rounded-full text-green-600 animate-bounce">
            <CheckCircle className="h-16 w-16" />
          </div>
        </div>
        
        <div className="space-y-2">
          <h1 className="text-4xl font-black text-gray-900 tracking-tight">Payment Successful!</h1>
          <p className="text-gray-500 text-lg font-medium">Your tech is officially reserved. Processing starts now.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
           <div className="bg-gray-50 p-6 rounded-3xl border border-gray-100 text-left">
              <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">Order Ref</p>
              <p className="text-xl font-black text-blue-600">#{orderId}</p>
           </div>
           <div className="bg-gray-50 p-6 rounded-3xl border border-gray-100 text-left">
              <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">Method</p>
              <p className="text-xl font-black text-gray-900">{order?.paymentMethod || 'SECURE PAY'}</p>
           </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
          <button 
            onClick={() => setShowInvoice(true)}
            className="flex items-center justify-center gap-2 bg-white border-2 border-gray-100 px-8 py-4 rounded-2xl font-bold hover:bg-gray-50 transition-all active:scale-95"
          >
            <FileText className="h-5 w-5" /> View Tax Invoice
          </button>
          <button 
            onClick={onNavigateOrders}
            className="flex items-center justify-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-2xl font-bold shadow-xl shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95"
          >
            <Package className="h-5 w-5" /> Track Order <ArrowRight className="h-5 w-5" />
          </button>
        </div>

        <button onClick={onNavigateHome} className="text-gray-400 text-sm font-bold hover:text-gray-900 transition-colors">
          Back to Storefront
        </button>
      </div>

      {/* Invoice Modal */}
      {showInvoice && order && store && (
        <div className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-2xl rounded-[40px] shadow-2xl overflow-hidden animate-in slide-in-from-bottom-20 duration-500">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <div className="flex items-center gap-2 font-black text-gray-900 uppercase tracking-tighter">
                    <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-sm">L</span> Tax Invoice
                 </div>
                 <button onClick={() => setShowInvoice(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all">
                    <X className="h-6 w-6 text-gray-400" />
                 </button>
              </div>

              <div className="p-10 max-h-[70vh] overflow-y-auto space-y-8">
                 <div className="flex justify-between items-start">
                    <div>
                       <h2 className="text-3xl font-black text-gray-900">{store.name}</h2>
                       <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Order #{order.id}</p>
                       {store.taxConfig?.enabled && (
                          <div className="mt-4 p-4 bg-gray-50 rounded-2xl border border-gray-100 inline-block">
                             <p className="text-[8px] text-gray-400 font-black uppercase tracking-widest">Seller Details</p>
                             <p className="text-xs font-bold text-gray-900 mt-1">{store.taxConfig.taxNumber || 'GST Exempted'}</p>
                             <p className="text-[10px] text-gray-500 mt-1 w-48 leading-relaxed">{store.taxConfig.businessAddress}</p>
                          </div>
                       )}
                    </div>
                    <div className="text-right">
                       <p className="text-xs text-gray-400 font-black uppercase tracking-widest">Date</p>
                       <p className="text-sm font-bold">{new Date(order.createdAt).toLocaleDateString()}</p>
                    </div>
                 </div>

                 <div className="space-y-4">
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest border-b border-gray-100 pb-2">Order Items</p>
                    <div className="space-y-3">
                       {order.items?.map((item, idx) => (
                         <div key={idx} className="flex justify-between items-center text-sm font-medium">
                            <p className="text-gray-900">{item.product.name} <span className="text-gray-400 ml-2">x{item.quantity}</span></p>
                            <p className="font-black text-gray-900">${(item.product.price * item.quantity).toFixed(2)}</p>
                         </div>
                       ))}
                    </div>
                 </div>

                 <div className="bg-gray-50 p-8 rounded-[32px] space-y-4">
                    <div className="flex justify-between text-sm text-gray-500 font-bold">
                       <span>Subtotal</span>
                       <span className="text-gray-900">${order.subtotal.toFixed(2)}</span>
                    </div>
                    {order.coinsRedeemed ? (
                      <div className="flex justify-between text-sm text-orange-600 font-bold">
                         <span>Loyalty Redemption</span>
                         <span>-${(order.coinsRedeemed * 0.1).toFixed(2)}</span>
                      </div>
                    ) : null}
                    <div className="flex justify-between text-sm text-gray-500 font-bold">
                       <span>Tax ({order.taxRate}%)</span>
                       <span className="text-gray-900">${order.taxAmount.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-gray-200 pt-4 flex justify-between items-center">
                       <span className="text-xl font-black text-gray-900">Total Paid</span>
                       <span className="text-3xl font-black text-blue-600">${order.total.toFixed(2)}</span>
                    </div>
                 </div>

                 <div className="flex items-center gap-4 text-emerald-600 font-bold text-xs p-4 bg-emerald-50 rounded-2xl">
                    <Landmark className="h-4 w-4" /> This is a computer-generated tax invoice. No signature required.
                 </div>
              </div>

              <div className="p-8 bg-gray-50 flex gap-4">
                 <button className="flex-grow bg-gray-900 text-white py-4 rounded-2xl font-black text-sm flex items-center justify-center gap-2">
                    <Download className="h-4 w-4" /> Download PDF
                 </button>
                 <button className="px-6 py-4 border-2 border-gray-200 rounded-2xl text-gray-400 hover:text-gray-900 transition-all">
                    <Printer className="h-4 w-4" />
                 </button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default OrderSuccess;
